import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { TablePlugin } from '@udecode/plate-table/react';
import { useEditorPlugin, useEditorSelector } from '@udecode/plate/react';
import { Dropdown } from '../Dropdown';
import { MarkdownTableIcon, ColumnsBIcon, RowsBIcon, PlusIcon, TrashBIcon } from '../Icons';
import { Stack } from '../Layout';
import { theme } from '../theme';
import { StyledButton, StyledIconWithLabel, StyledToolbarArrowIcon } from './styled';
import { focusEditorAfterOperation, isInsideCodeBlock } from './utils';
function IconWithLabel({ icon, label, disabled }) {
    return /*#__PURE__*/ _jsxs(StyledIconWithLabel, {
        direction: "horizontal",
        gap: theme.spaces.x8,
        alignY: "center",
        disabled: disabled,
        children: [
            icon,
            /*#__PURE__*/ _jsx("span", {
                children: label
            })
        ]
    });
}
export function TableActions() {
    const { editor, tf } = useEditorPlugin(TablePlugin);
    const tableSelected = useEditorSelector((editor)=>editor.api.some({
            match: {
                type: TablePlugin.key
            }
        }), []);
    return /*#__PURE__*/ _jsx(Dropdown, {
        "data-test-id": "markdown-editor-table-actions-dropdown",
        overlay: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Dropdown.SubMenu, {
                    "data-test-id": "markdown-editor-table-actions-dropdown-table-submenu",
                    overlay: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Dropdown.Item, {
                                onClick: ()=>{
                                    if (isInsideCodeBlock(editor)) {
                                        focusEditorAfterOperation(editor);
                                        return;
                                    }
                                    focusEditorAfterOperation(editor, ()=>{
                                        tf.insert.table({
                                            header: true,
                                            rowCount: 3,
                                            colCount: 3
                                        });
                                    });
                                },
                                "data-test-id": "markdown-editor-table-actions-dropdown-insert-table-dropdown-item",
                                children: /*#__PURE__*/ _jsx(IconWithLabel, {
                                    icon: /*#__PURE__*/ _jsx(PlusIcon, {
                                        type: "default"
                                    }),
                                    label: "Insert Table"
                                })
                            }),
                            /*#__PURE__*/ _jsx(Dropdown.Item, {
                                disabled: !tableSelected,
                                onClick: ()=>{
                                    focusEditorAfterOperation(editor, ()=>{
                                        tf.remove.table();
                                    });
                                },
                                "data-test-id": "markdown-editor-table-actions-dropdown-delete-table-dropdown-item",
                                children: /*#__PURE__*/ _jsx(IconWithLabel, {
                                    icon: /*#__PURE__*/ _jsx(TrashBIcon, {
                                        type: "default"
                                    }),
                                    label: "Delete Table",
                                    disabled: !tableSelected
                                })
                            })
                        ]
                    }),
                    children: /*#__PURE__*/ _jsx(IconWithLabel, {
                        icon: /*#__PURE__*/ _jsx(MarkdownTableIcon, {
                            type: "extended"
                        }),
                        label: "Table"
                    })
                }),
                /*#__PURE__*/ _jsx(Dropdown.SubMenu, {
                    "data-test-id": "markdown-editor-table-actions-dropdown-columns-submenu",
                    disabled: !tableSelected,
                    overlay: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Dropdown.Item, {
                                onClick: ()=>{
                                    focusEditorAfterOperation(editor, ()=>{
                                        tf.insert.tableColumn();
                                    });
                                },
                                "data-test-id": "markdown-editor-table-actions-dropdown-insert-column-dropdown-item",
                                children: /*#__PURE__*/ _jsx(IconWithLabel, {
                                    icon: /*#__PURE__*/ _jsx(PlusIcon, {
                                        type: "default"
                                    }),
                                    label: "Insert Column"
                                })
                            }),
                            /*#__PURE__*/ _jsx(Dropdown.Item, {
                                onClick: ()=>{
                                    focusEditorAfterOperation(editor, ()=>{
                                        tf.remove.tableColumn();
                                    });
                                },
                                "data-test-id": "markdown-editor-table-actions-dropdown-delete-column-dropdown-item",
                                children: /*#__PURE__*/ _jsx(IconWithLabel, {
                                    icon: /*#__PURE__*/ _jsx(TrashBIcon, {
                                        type: "default"
                                    }),
                                    label: "Delete Column"
                                })
                            })
                        ]
                    }),
                    children: /*#__PURE__*/ _jsx(IconWithLabel, {
                        icon: /*#__PURE__*/ _jsx(ColumnsBIcon, {
                            type: "default"
                        }),
                        label: "Column",
                        disabled: !tableSelected
                    })
                }),
                /*#__PURE__*/ _jsx(Dropdown.SubMenu, {
                    disabled: !tableSelected,
                    overlay: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Dropdown.Item, {
                                onClick: ()=>{
                                    focusEditorAfterOperation(editor, ()=>{
                                        tf.insert.tableRow();
                                    });
                                },
                                "data-test-id": "markdown-editor-table-actions-dropdown-insert-row-dropdown-item",
                                children: /*#__PURE__*/ _jsx(IconWithLabel, {
                                    icon: /*#__PURE__*/ _jsx(PlusIcon, {
                                        type: "default"
                                    }),
                                    label: "Insert Row"
                                })
                            }),
                            /*#__PURE__*/ _jsx(Dropdown.Item, {
                                onClick: ()=>{
                                    focusEditorAfterOperation(editor, ()=>{
                                        tf.remove.tableRow();
                                    });
                                },
                                "data-test-id": "markdown-editor-table-actions-dropdown-delete-row-dropdown-item",
                                children: /*#__PURE__*/ _jsx(IconWithLabel, {
                                    icon: /*#__PURE__*/ _jsx(TrashBIcon, {
                                        type: "default"
                                    }),
                                    label: "Delete Row"
                                })
                            })
                        ]
                    }),
                    "data-test-id": "markdown-editor-table-actions-dropdown-rows-dropdown-submenu",
                    children: /*#__PURE__*/ _jsx(IconWithLabel, {
                        icon: /*#__PURE__*/ _jsx(RowsBIcon, {
                            type: "default"
                        }),
                        label: "Rows",
                        disabled: !tableSelected
                    })
                })
            ]
        }),
        children: /*#__PURE__*/ _jsx(StyledButton, {
            variant: "tertiaryGray",
            width: theme.sizes.x56,
            size: "m",
            icon: /*#__PURE__*/ _jsxs(Stack, {
                direction: "horizontal",
                gap: theme.spaces.x4,
                alignY: "center",
                children: [
                    /*#__PURE__*/ _jsx(MarkdownTableIcon, {
                        type: "extended"
                    }),
                    /*#__PURE__*/ _jsx(StyledToolbarArrowIcon, {
                        type: "default"
                    })
                ]
            }),
            "aria-label": "Table options menu",
            "data-test-id": "markdown-editor-table-button"
        })
    });
}
